<!--
  `static` below `md` is deliberate (FEC-1150 QA): it hands the panel's
  positioning over to the nearest positioned ancestor — the sticky <header> —
  so the dropdown can span the full viewport instead of hanging off the left
  edge from a bell that sits mid-row. From `md` up the bell is the anchor again
  and the panel is a normal right-aligned popover.
-->
<div class="static md:relative">
  <button
    type="button"
    class="relative flex items-center justify-center bg-white rounded-full size-9 md:size-11 cursor-pointer transition-fec hover:bg-muted dark:bg-dark dark:hover:bg-dark-grey focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dark dark:focus-visible:ring-white"
    [attr.aria-label]="
      unreadCount() > 0
        ? 'Announcements, ' + unreadCount() + ' unread'
        : 'Announcements, none unread'
    "
    [attr.aria-expanded]="isOpen()"
    aria-haspopup="true"
    title="Announcements"
    (click)="toggle()"
  >
    <app-icon name="announcement" [size]="18" class="text-dark dark:text-white" />

    @if (unreadCount() > 0) {
      <span
        class="absolute -right-0.5 -top-0.5 flex min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-4 text-white"
        data-testid="announcement-unread-badge"
      >
        {{ badgeLabel() }}
      </span>
    }
  </button>

  @if (isOpen()) {
    <div
      class="absolute inset-x-4 top-[calc(100%+8px)] z-50 overflow-hidden rounded-2xl bg-white shadow-xl md:inset-x-auto md:right-0 md:w-[340px] md:max-w-[calc(100vw-2rem)] dark:bg-surface-dark"
      role="dialog"
      aria-label="Announcements"
    >
      <div
        class="flex items-center justify-between gap-2 border-b border-solid border-b-dark/5 px-4 py-3"
      >
        <p class="text-sm font-semibold text-dark dark:text-white">Announcements</p>
        @if (unreadCount() > 0) {
          <span class="text-xs text-dark-grey dark:text-grey">{{ unreadCount() }} unread</span>
        }
      </div>

      @if (unreadCount() > 0) {
        <div class="flex justify-end border-b border-solid border-b-dark/5 px-4 py-2">
          <button
            type="button"
            class="cursor-pointer text-xs font-medium text-dark underline underline-offset-2 disabled:opacity-50 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dark dark:text-white dark:focus-visible:ring-white"
            [disabled]="isMarkingAll()"
            (click)="markAllAsRead()"
          >
            Mark all as read
          </button>
        </div>
      }

      <ul class="max-h-[360px] overflow-y-auto">
        @for (announcement of announcements(); track announcement.id) {
          <li>
            <button
              type="button"
              class="flex w-full cursor-pointer items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-dark/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-dark dark:hover:bg-white/5 dark:focus-visible:ring-white"
              (click)="openAnnouncement(announcement)"
            >
              <span class="mt-1.5 flex size-2 shrink-0" aria-hidden="true">
                <span
                  [class]="
                    announcement.is_read
                      ? 'size-2 rounded-full bg-dark dark:bg-white'
                      : 'size-2 rounded-full bg-primary'
                  "
                ></span>
              </span>

              <span class="min-w-0 flex-1">
                <!--
                  Date/time sits beside the title, not just buried in the
                  project/date line below it (FEC-1161 QA, 17 Aug).
                -->
                <span class="flex items-start justify-between gap-2">
                  <span class="truncate text-sm font-semibold text-dark dark:text-white">
                    {{ announcement.title }}
                  </span>
                  <span
                    class="shrink-0 whitespace-nowrap text-[11px] text-dark-grey dark:text-grey"
                  >
                    {{ announcement.published_at | date: 'd MMM, h:mm a' }}
                  </span>
                </span>
                <span class="mt-0.5 block text-xs text-dark-grey dark:text-grey">
                  {{ messagePreview(announcement) }}
                </span>
                <span class="mt-1 block text-[11px] text-dark-grey dark:text-grey">
                  {{ announcement.project?.name }}
                </span>
              </span>
            </button>
          </li>
        } @empty {
          <li class="px-4 py-8 text-center text-sm text-dark-grey dark:text-grey">
            @if (isLoading()) {
              Loading announcements…
            } @else {
              You have no announcements yet.
            }
          </li>
        }
      </ul>
    </div>
  }
</div>
